/* eslint-disable max-len */
import styled, { css } from 'styled-components';
import {
  FilterChip,
  Flex,
  mediaBreakpointMixin,
  Page,
  spaceMixin,
  Tabs,
  ToggleButtonGroup,
  token,
} from '@zillow/constellation';
import { SETTINGS } from '@zg-rentals/zrm-utils-js';

const maxSearchBoxWidth = '370px';

export const StyledPage = styled(Page)`
  &.Page-no-padding {
    padding-bottom: 62px;
  }

  .Container {
    padding: 0 8px;
  }

  .PropertiesPageSkeleton {
    padding-top: 0;
  }

  .PropertiesPage-display-container {
    position: relative;
  }

  .PropertiesPage-ListingViewSwitcher {
    margin-bottom: 4px;
  }
`;

export const StyledPageContainer = styled.div<{ isZram: boolean }>`
  ${({ isZram }) =>
    !isZram &&
    css`
      background-color: ${token('colors.white')};
      border-radius: ${spaceMixin('xs')};
      border: 1px solid ${token('colors.gray300')};
    `}
`;

export const StyledFlexContainer = styled(Flex)<{ isZram: boolean }>`
  flex-direction: column;
  min-height: ${({ isZram }) => (isZram ? '94px' : '28px')};
  ${({ isZram }) =>
    isZram &&
    css`
      background: ${token('colors.white')};
      border: solid 1px ${token('colors.gray300')};
      border-radius: 3px;
    `}
  justify-content: space-evenly;

  gap: ${spaceMixin('sm')} 0;

  @media ${(props) => mediaBreakpointMixin(props.wrapAtXL ? 'xl' : 'lg')} {
    justify-content: space-between;
    flex-direction: row;
  }
`;

export const StyledRightSubFlexContainer = styled.div<{ wrapAtXL: boolean }>`
  display: flex;
  flex-grow: 2;
  justify-content: center;
  flex-direction: row;
  flex-wrap: nowrap;
  align-items: center;
  order: 0;
  width: 100%;

  @media ${(props) => mediaBreakpointMixin(props.wrapAtXL ? 'xl' : 'lg')} {
    order: 5;
    justify-content: flex-end;
  }
`;

export const StyledSearchBoxContainer = styled.div`
  width: 100%;
  max-width: ${maxSearchBoxWidth};
`;

export const StyledTabsContainer = styled.div<{
  wrapAtXL: boolean;
  isZram: boolean;
  canViewSearch: boolean;
}>`
  max-width: 100%;

  @media ${(props) => mediaBreakpointMixin(props.wrapAtXL ? 'xl' : 'lg')} {
    max-width: ${({ canViewSearch }) => (canViewSearch ? '50%' : '100%')};
    margin-right: ${({ isZram }) => (isZram ? '0' : spaceMixin('sm'))};
  }

  @media ${(props) => mediaBreakpointMixin(props.wrapAtXL ? 'lg_lte' : 'md_lte')} {
    margin-top: ${({ isZram, canViewSearch }) => (!isZram && canViewSearch ? spaceMixin('sm') : '0')};
  }
`;

export const StyledTabsList = styled(Tabs.List)`
  border: none;
`;

export const StyledTab = styled(Tabs.Tab)`
  &&[aria-selected='true'] {
    color: ${(props) => (props.isActive && !props.disabled ? token('colors.brand') : token('colors.black'))};
  }

  @media ${(props) => mediaBreakpointMixin(props.wrapAtXL ? 'xl' : 'lg')} {
    &&[aria-selected='true']:before {
      background-color: transparent;
    }
  }
`;

export const StyledFilter = styled(FilterChip)`
  &&& {
    margin: 0px ${spaceMixin('xs')};
    border-radius: 999px;
  }
`;

export const StyledStickySubHeadingSectionContainer = styled.div<{
  isVisible?: boolean;
}>`
  /*
   * if placeholder is visible, then is static
   * else fixed
   */
  ${(props) =>
    props.isVisible
      ? css`
          position: static;
          box-shadow: 0;
        `
      : css`
          position: fixed;
          box-shadow: 0px 2px 2px rgba(0, 0, 0, 0.26);
          border-bottom: 1px solid ${token('colors.borderLight')};
          background-color: ${token('colors.white')};
        `}

  top: 0;
  left: 0;
  width: 100%;
  z-index: ${token('zIndices.modal')};

  /* 1024 (breakpoint xl) */
  @media (min-width: ${SETTINGS.BREAKPOINTS_XL}px) {
    left: ${(props) => (!props.isVisible ? `${SETTINGS.LEFT_NAV_WIDTH}px` : 0)};
    width: ${(props) => (!props.isVisible ? `calc(100% - ${SETTINGS.LEFT_NAV_WIDTH}px)` : '100%')};
  }
`;

export const StyledToggleButtonGroup = styled(ToggleButtonGroup)<{ isScreenLgGTE?: boolean }>`
  flex-wrap: ${({ isScreenLgGTE = true }) => (isScreenLgGTE ? 'nowrap' : 'wrap')};
  gap: ${spaceMixin('xs')} 0;
`;
